import { isOpenForInput } from './proposal-display'
import { ENDED_UNSIGNED } from './proposals-views'

/**
 * The proposal detail page's sections and its "next step" panel (ENT-565, Sales design).
 *
 * The sections are tabs whose choice lives in the URL (`?section=`), so a deep link and the back button
 * open the same one. History is not offered yet: crm-svc has no timeline read for a proposal.
 */
export const PROPOSAL_DETAIL_SECTIONS = ['summary', 'pdf', 'signing'] as const
export type ProposalDetailSection = (typeof PROPOSAL_DETAIL_SECTIONS)[number]

export const PROPOSAL_DETAIL_SECTION_LABEL: Record<ProposalDetailSection, string> = {
  summary: 'Summary',
  pdf: 'Proposal PDF',
  signing: 'Signing',
}

export interface ProposalDetailSearch {
  section?: ProposalDetailSection
}

/** The route's `validateSearch`: an unknown or missing section is the Summary (left out of the URL). */
export function validateProposalDetailSearch(search: Record<string, unknown>): ProposalDetailSearch {
  const s = search.section
  return typeof s === 'string' && (PROPOSAL_DETAIL_SECTIONS as readonly string[]).includes(s) && s !== 'summary'
    ? { section: s as ProposalDetailSection }
    : {}
}

/** Where the panel's button goes: the inputs form, or the section of this page that holds the action. */
export type ProposalNextTarget = 'edit' | 'pdf' | 'signing'

export interface ProposalNextAction {
  title: string
  note: string
  /** No button when nothing is to be done from here (the page is the action, or the record is closed). */
  action: { label: string; target: ProposalNextTarget } | null
  tone: 'info' | 'warning' | 'success'
}

/**
 * What the seller does next, from the proposal's state and its latest signing delivery. The mutations
 * themselves stay where they are (the PDF section renders and submits, the Signing section sends and
 * retries): the panel only says what is next and opens the section that does it.
 */
export function proposalNextAction(
  state: string,
  signingState: string | null | undefined,
  opts: { returned: boolean; locked: boolean; notice?: string | null },
): ProposalNextAction {
  if (opts.locked && state !== 'ACCEPTED') {
    return { title: 'Read-only record', note: 'This proposal can no longer be changed.', action: null, tone: 'info' }
  }
  if (isOpenForInput(state)) {
    return opts.returned
      ? {
          title: 'Changes requested',
          note: 'A manager returned this round. Make the requested changes, then lock the inputs again.',
          action: { label: 'Make changes', target: 'edit' },
          tone: 'info',
        }
      : {
          title: 'Continue your proposal',
          note: 'Complete the details and discount, then lock the inputs.',
          action: { label: 'Continue draft', target: 'edit' },
          tone: 'info',
        }
  }
  switch (state) {
    case 'INPUT_SUBMITTED':
      return {
        title: 'Create the proposal PDF',
        note: 'Generate the PDF from the locked inputs, then check it before requesting approval.',
        action: { label: 'Generate PDF', target: 'pdf' },
        tone: 'info',
      }
    case 'RENDERED':
      return {
        title: 'Check the PDF, then request approval',
        note: 'The manager reviews this exact version and its discount.',
        action: { label: 'Request approval', target: 'pdf' },
        tone: 'info',
      }
    case 'PENDING_APPROVAL':
      return {
        title: 'Waiting for a Sales Manager',
        note: 'You can review the submitted version, or open a new round if a change is needed.',
        action: { label: 'View submitted version', target: 'pdf' },
        tone: 'info',
      }
    case 'ACCEPTED':
      return {
        title: 'Accepted',
        note: 'The client signed this proposal. The signed copy is kept with it.',
        action: { label: 'View signed copy', target: 'signing' },
        tone: 'success',
      }
    case 'APPROVED':
      if (signingState === 'REQUEST_FAILED') {
        return {
          title: 'The signing request failed',
          note: 'Send the request for the approved version again.',
          action: { label: 'Retry signing request', target: 'signing' },
          tone: 'warning',
        }
      }
      if (signingState === 'REQUEST_UNCONFIRMED') {
        return {
          title: 'Confirm delivery before trying again',
          note: 'A request may already exist. Check its status to avoid sending the client a second one.',
          action: { label: 'Check status', target: 'signing' },
          tone: 'warning',
        }
      }
      if (signingState && ENDED_UNSIGNED.includes(signingState)) {
        return {
          title: 'The client did not sign',
          note: 'Review the situation and start a new round before requesting a signature again.',
          action: null,
          tone: 'warning',
        }
      }
      if (!signingState || signingState === 'SUPERSEDED') {
        return {
          title: 'Approved and ready for signature',
          note: 'Only this approved version can be sent to the client.',
          action: { label: 'Send for signature', target: 'signing' },
          tone: 'success',
        }
      }
      // The request stands, but the email with the link did not (or may not have) reached the client: it
      // is not sent again automatically, so the sales user must know before waiting on a signature.
      if (opts.notice === 'EMAIL_FAILED') {
        return {
          title: 'The signing email did not reach the client',
          note: 'The signature request stands, but the email with the signing link failed and is not sent again. To send a new request, revise the proposal.',
          action: { label: 'View signing details', target: 'signing' },
          tone: 'warning',
        }
      }
      if (opts.notice === 'EMAIL_UNCONFIRMED') {
        return {
          title: 'The signing email is not confirmed',
          note: 'It is not known whether the client received the signing link. Do not assume they have it.',
          action: { label: 'View signing details', target: 'signing' },
          tone: 'warning',
        }
      }
      return {
        title: 'Waiting for the client to sign',
        note: 'The approved version is fixed. Open the signing details to follow the request.',
        action: { label: 'View signing details', target: 'signing' },
        tone: 'info',
      }
    default:
      return { title: 'Proposal details', note: 'Review the current proposal.', action: null, tone: 'info' }
  }
}

/** The "Current step" line of the People & responsibility card. */
export function proposalCurrentStep(state: string): string {
  if (isOpenForInput(state) || state === 'INPUT_SUBMITTED' || state === 'RENDERED') return 'Sales preparation'
  if (state === 'PENDING_APPROVAL') return 'Manager review'
  if (state === 'APPROVED') return 'Client signature'
  if (state === 'ACCEPTED') return 'Complete'
  return 'Closed'
}

/**
 * List cells that hold money (the line items' price and discount, an extra charge's amount): shown
 * right-aligned with the server's decimal string grouped as text. Never parsed or summed (FR-67).
 */
const MONEY_CELL_KEYS = new Set(['price', 'discount', 'amount', 'gross', 'net'])
export function isMoneyCell(key: string): boolean {
  return MONEY_CELL_KEYS.has(key)
}

/** List cells that hold a plain number (a line's quantity, an owner's share): right-aligned like money. */
const NUMBER_CELL_KEYS = new Set(['quantity', 'sharePct'])
export function isNumberCell(key: string): boolean {
  return NUMBER_CELL_KEYS.has(key)
}

/** The smallest shape of a server input field the card grouping needs. */
interface GroupableField {
  key: string
  value?: string | null
  rows?: readonly unknown[] | null
}

/**
 * How a Summary card lays its fields out: `fields` a label/value grid, `pricing` the line items table,
 * `lists` the compact owner, activity and scope lists.
 */
export type ProposalSummaryLayout = 'fields' | 'pricing' | 'lists'

export interface ProposalSummaryCard<F extends GroupableField> {
  key: string
  label: string
  layout: ProposalSummaryLayout
  fields: F[]
  /** True when an empty field is left out instead of shown as "Not provided". */
  hideEmpty: boolean
}

/**
 * The Sales design's Summary cards, built from the wizard steps the server sends. The wizard is
 * configuration-driven, so only the step keys of the default configuration are grouped; any other
 * step keeps a card of its own under its own label, after the design's cards and before the requirements.
 *
 * - Company setup: the jurisdiction step, the line items step's plain fields (the visa count) and the
 *   basics step. The deal reference is left out: the page title already shows it.
 * - Line items: the line items list.
 * - Owners, activities & scope: the three list steps, as compact lists.
 * - Proposal requirements: the terms step, only the fields that hold a value (most are optional).
 * - Any other step (the extra charges): only once one of its fields holds a value.
 */
const SETUP_STEPS = ['jurisdiction', 'lineItems', 'basics']
const PEOPLE_STEPS = new Set(['owners', 'activities', 'scope'])
const HIDDEN_FIELDS = new Set(['subject.dealReference'])
/** The design's core cards, shown even while empty; any other card only once something is entered. */
const ALWAYS_SHOWN = new Set(['setup', 'lineItems', 'people'])
const CARD_RANK: Record<string, number> = { setup: 0, lineItems: 1, people: 2, other: 3, terms: 4 }

export function proposalSummaryCards<F extends GroupableField>(
  sections: readonly { key: string; label: string; fields: readonly F[] }[],
): ProposalSummaryCard<F>[] {
  const cards: ProposalSummaryCard<F>[] = []
  const setupParts = new Map<string, F[]>()
  const card = (key: string, make: () => Omit<ProposalSummaryCard<F>, 'key' | 'fields'>) => {
    let found = cards.find((c) => c.key === key)
    if (!found) {
      found = { key, fields: [], ...make() }
      cards.push(found)
    }
    return found
  }
  for (const section of sections) {
    for (const field of section.fields) {
      if (HIDDEN_FIELDS.has(field.key)) continue
      const list = field.rows != null
      if (SETUP_STEPS.includes(section.key) && !list) {
        card('setup', () => ({ label: 'Company setup', layout: 'fields', hideEmpty: false }))
        setupParts.set(section.key, [...(setupParts.get(section.key) ?? []), field])
      } else if (section.key === 'lineItems') {
        card(section.key, () => ({ label: section.label, layout: 'pricing', hideEmpty: false })).fields.push(field)
      } else if (PEOPLE_STEPS.has(section.key)) {
        card('people', () => ({ label: 'Owners, activities & scope', layout: 'lists', hideEmpty: false })).fields.push(field)
      } else if (section.key === 'terms') {
        card(section.key, () => ({ label: 'Proposal requirements', layout: 'fields', hideEmpty: true })).fields.push(field)
      } else {
        card(section.key, () => ({ label: section.label, layout: 'fields', hideEmpty: false })).fields.push(field)
      }
    }
  }
  const setup = cards.find((c) => c.key === 'setup')
  if (setup) setup.fields = SETUP_STEPS.flatMap((step) => setupParts.get(step) ?? [])
  // The design's order; a card of any other step sits before the requirements, in the server's order.
  const rank = (key: string) => CARD_RANK[key] ?? CARD_RANK.other
  return [...cards]
    .sort((a, b) => rank(a.key) - rank(b.key))
    .map((c) => (c.hideEmpty ? { ...c, fields: c.fields.filter(hasValue) } : c))
    .filter((c) => c.fields.length > 0 && (ALWAYS_SHOWN.has(c.key) || c.fields.some(hasValue)))
}

const hasValue = (f: GroupableField) => (f.rows != null ? f.rows.length > 0 : !!f.value)
